import { z } from 'zod';
import { productKeySchema } from './products.js';
/**
 * Return-path rules shared by every app and by platform-api.
 *
 * A return path is the place inside a product a person asked for before sign-in ("deep link"). It is
 * carried through the sign-in transaction and later turned into a URL on a REGISTERED application
 * origin. It is the classic open-redirect vector, so the rules are strict and fail closed:
 * only a same-origin relative path survives; anything that could be read as another origin, smuggle a
 * scheme, carry credentials, re-enter the sign-in flow or replay an authorization response is rejected.
 * Callers fall back to the application's registered default path on rejection.
 */
export const MAX_RETURN_PATH_LENGTH = 2048;
/** OAuth/OIDC response parameters that must never be replayed into ordinary navigation. */
export const AUTH_RESPONSE_PARAMS = [
    'code',
    'state',
    'error',
    'error_description',
    'error_uri',
    'session_state',
    'iss',
];
/**
 * Routes that would loop back into authentication, or end the session, if used as a destination.
 * /session-expired is here because the hub clears its session on it, exactly like /signed-out.
 */
export const RESERVED_AUTH_PATHS = [
    '/login',
    '/auth',
    '/logout',
    '/sign-out',
    '/signout',
    '/signed-out',
    '/session-expired',
];
/**
 * Rounds of percent-decoding applied to a pathname before the structural checks run on it. Routers and
 * proxies decode once, sometimes twice; a pathname that still changes after this many rounds is refused
 * as an encoded separator instead of being guessed at.
 */
export const MAX_RETURN_PATH_DECODE_ROUNDS = 3;
export const RETURN_PATH_REJECTIONS = [
    'not_a_string',
    'empty',
    'too_long',
    'control_characters',
    'backslash',
    'not_relative',
    'protocol_relative',
    'encoded_separator',
    'malformed_encoding',
    'credentials',
    'auth_route',
    'unparseable',
];
// Control characters (C0 and DEL). URL parsers silently strip tab and newline, which is how
// "/\t/evil.example" becomes "//evil.example", so they are rejected before parsing.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;
const TOKEN_IN_FRAGMENT = /(^#|&)(access_token|id_token|refresh_token|code|state)=/i;
const PARSE_BASE = 'https://return-path.invalid';
const fail = (reason) => ({ ok: false, reason });
function safeDecode(value) {
    try {
        return decodeURIComponent(value);
    }
    catch {
        return null;
    }
}
export function isReservedAuthPath(pathname) {
    const lower = pathname.toLowerCase();
    return RESERVED_AUTH_PATHS.some((p) => lower === p || lower.startsWith(`${p}/`));
}
/**
 * One round of decoding, segment by segment, the way a router decodes path parameters. A segment that
 * is not valid percent-encoding stays as it is (a router keeps it literal or refuses it), so one bad
 * segment never hides what the others decode to.
 */
function decodeSegments(pathname) {
    return pathname
        .split('/')
        .map((segment) => safeDecode(segment) ?? segment)
        .join('/');
}
/** RFC 3986 remove_dot_segments for an absolute path ("/a/../login" -> "/login"). */
function removeDotSegments(pathname) {
    const parts = pathname.split('/');
    const out = [];
    parts.forEach((part, index) => {
        const last = index === parts.length - 1;
        if (part === '.' || part === '..') {
            if (part === '..' && out.length > 1)
                out.pop();
            if (last)
                out.push('');
            return;
        }
        out.push(part);
    });
    return out.join('/');
}
/**
 * The pathname as parsed, followed by each decoded form until decoding stops changing it. Null when it
 * is still changing after MAX_RETURN_PATH_DECODE_ROUNDS rounds.
 */
function decodedForms(pathname) {
    const forms = [pathname];
    for (let round = 0; round < MAX_RETURN_PATH_DECODE_ROUNDS; round += 1) {
        const current = forms[forms.length - 1];
        const next = decodeSegments(current);
        if (next === current)
            return forms;
        forms.push(next);
    }
    const last = forms[forms.length - 1];
    return decodeSegments(last) === last ? forms : null;
}
/**
 * Structural checks on one form of the pathname. They run on every decoded form, with dot segments
 * resolved, because that is what React Router, Express and most proxies act on: "/%6cogout" is routed
 * as "/logout", and "/a/../%2f%2fevil.example" as "//evil.example". A decoded "?" or "#" also ends the
 * path for these checks, because a proxy that decodes and forwards the URL turns "/login%3Fx" into
 * "/login" with a query.
 */
function formRejection(form) {
    if (CONTROL_CHARS.test(form))
        return 'control_characters';
    if (form.includes('\\'))
        return 'backslash';
    const beforeQuery = form.replace(/[?#][\s\S]*$/, '');
    const routed = [form, beforeQuery].flatMap((path) => [path, removeDotSegments(path)]);
    if (routed.some((path) => path.startsWith('//')))
        return 'protocol_relative';
    if (routed.some(isReservedAuthPath))
        return 'auth_route';
    return null;
}
/**
 * Validates a return path (see the rules at the top of this file). The accepted path is a fixpoint:
 * parseReturnPath(result.path) returns exactly the same result. That is checked, not assumed, so a later
 * pass (the API, the session app state, the hub's router state) can never reject or change what an
 * earlier pass accepted, and no consumer has to re-validate to stay safe.
 */
export function parseReturnPath(input) {
    const first = parseOnce(input);
    if (!first.ok)
        return first;
    const again = parseOnce(first.path);
    if (!again.ok)
        return again;
    return again.path === first.path ? first : fail('unparseable');
}
function parseOnce(input) {
    if (typeof input !== 'string')
        return fail('not_a_string');
    if (input.length === 0)
        return fail('empty');
    if (input.length > MAX_RETURN_PATH_LENGTH)
        return fail('too_long');
    if (CONTROL_CHARS.test(input))
        return fail('control_characters');
    if (input.includes('\\'))
        return fail('backslash');
    const once = safeDecode(input);
    if (once === null)
        return fail('malformed_encoding');
    if (CONTROL_CHARS.test(once))
        return fail('control_characters');
    if (once.includes('\\'))
        return fail('backslash');
    if (!input.startsWith('/'))
        return fail('not_relative');
    if (input.startsWith('//') || once.startsWith('//'))
        return fail('protocol_relative');
    // A second decode catches double-encoded separators ("/%252f%252fevil.example") that a
    // downstream router or proxy might decode again.
    const twice = safeDecode(once) ?? once;
    if (twice.startsWith('//') || twice.includes('\\') || CONTROL_CHARS.test(twice))
        return fail('encoded_separator');
    let url;
    try {
        url = new URL(input, PARSE_BASE);
    }
    catch {
        return fail('unparseable');
    }
    if (url.origin !== PARSE_BASE)
        return fail('not_relative');
    if (url.username || url.password)
        return fail('credentials');
    // Dot segments are normalized by the parser: "/.//evil.example" becomes "//evil.example".
    if (url.pathname.startsWith('//'))
        return fail('protocol_relative');
    const forms = decodedForms(url.pathname);
    if (!forms)
        return fail('encoded_separator');
    for (const form of forms) {
        const rejection = formRejection(form);
        if (rejection)
            return fail(rejection);
    }
    let search = url.search;
    if (AUTH_RESPONSE_PARAMS.some((p) => url.searchParams.has(p))) {
        for (const p of AUTH_RESPONSE_PARAMS)
            url.searchParams.delete(p);
        const rebuilt = url.searchParams.toString();
        search = rebuilt ? `?${rebuilt}` : '';
    }
    const hash = TOKEN_IN_FRAGMENT.test(url.hash) ? '' : url.hash;
    const path = `${url.pathname}${search}${hash}`;
    // Normalizing percent-encodes non-ASCII and spaces, so the result can be up to nine times longer than
    // the input. The limit applies to what is returned as well, so the hub, the API (navigationRequestSchema)
    // and the session app state all accept or refuse the same values.
    if (path.length > MAX_RETURN_PATH_LENGTH)
        return fail('too_long');
    return { ok: true, path };
}
/** Returns the validated path, or the fallback when the candidate is missing or rejected. */
export function returnPathOrDefault(candidate, fallback) {
    if (candidate === undefined || candidate === null || candidate === '')
        return fallback;
    const result = parseReturnPath(candidate);
    return result.ok ? result.path : fallback;
}
/** Normalizes an origin ("https://Brokers.Trace.io:443" -> "https://brokers.trace.io"). Throws on non-origins. */
export function normalizeOrigin(value) {
    const url = new URL(value);
    if (url.protocol !== 'https:' && url.protocol !== 'http:')
        throw new Error('unsupported_scheme');
    if (url.username || url.password)
        throw new Error('credentials_in_origin');
    if (url.pathname !== '/' || url.search || url.hash)
        throw new Error('not_an_origin');
    return url.origin;
}
/**
 * Builds the absolute destination URL on a registered application origin. The path must already be
 * validated; the origin check here is the second, independent guard.
 */
export function buildDestinationUrl(registeredOrigin, validatedPath) {
    const origin = normalizeOrigin(registeredOrigin);
    const checked = parseReturnPath(validatedPath);
    if (!checked.ok)
        throw new Error(`invalid_return_path:${checked.reason}`);
    const target = new URL(checked.path, origin);
    if (target.origin !== origin)
        throw new Error('destination_origin_mismatch');
    return target.toString();
}
export const NAVIGATION_DENY_REASONS = [
    'membership_required',
    'membership_suspended',
    'organization_suspended',
    'product_not_entitled',
    'product_unknown',
    'portal_scope_required',
    'staff_required',
    'host_unknown',
    'host_mismatch',
    'context_not_found',
];
export const navigationDenyReasonSchema = z.enum(NAVIGATION_DENY_REASONS);
export const REAUTHENTICATE_REASONS = ['sso_required', 'mfa_required', 'organization_login_required'];
/** POST /v1/navigation/decide request. The host is taken from the browser Origin header, never the body. */
export const navigationRequestSchema = z.object({
    productKey: productKeySchema.optional(),
    returnPath: z.string().max(MAX_RETURN_PATH_LENGTH).optional(),
    contextId: z.string().max(200).optional(),
});
export const navigationDecisionSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('redirect'),
        productKey: productKeySchema,
        contextId: z.string(),
        url: z.url(),
    }),
    z.object({
        kind: z.literal('select-organization'),
        productKey: productKeySchema.optional(),
        returnPath: z.string().optional(),
    }),
    z.object({
        kind: z.literal('select-product'),
        contextId: z.string(),
        returnPath: z.string().optional(),
    }),
    z.object({
        kind: z.literal('access-denied'),
        reason: navigationDenyReasonSchema,
        productKey: productKeySchema.optional(),
        contextId: z.string().optional(),
    }),
    z.object({ kind: z.literal('no-workspace') }),
    z.object({
        kind: z.literal('unavailable'),
        productKey: productKeySchema,
        reason: z.enum(['not_deployed', 'disabled_in_environment']),
    }),
    z.object({
        kind: z.literal('reauthenticate'),
        reason: z.enum(REAUTHENTICATE_REASONS),
        productKey: productKeySchema.optional(),
        contextId: z.string().optional(),
        // Opaque identity-provider hints (an Auth0 organization id and connection name). Not secrets.
        organizationHint: z.string().optional(),
        connectionHint: z.string().optional(),
    }),
]);
//# sourceMappingURL=navigation.js.map